import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { EncryptionService } from 'src/app/services/encryption.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/services/common.service';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
    resetForm!: FormGroup;
    token: string | null = null;
    loading = false;
    invalidLink = false;
    errorMessage = '';
    newPasswordVisible = false;
    confirmPasswordVisible = false;
    language: any;
    currentLanguage: any = 'en';
    browserLang: any;
    languageCode = 'en';

    userName: string | null = null;
    userEmail: string | null = null;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private _service: ApiService,
        private encrypt: EncryptionService,
        private router: Router,
        private toastr: ToastrService,
        public translate: TranslateService,
        private commonService: CommonService
    ) {
        translate.addLangs(['en', 'fr', 'it']);
        translate.setDefaultLang('en');
        this.browserLang = translate.getBrowserLang();
        this.translate.setDefaultLang(this.browserLang);
        this.translate.use(this.browserLang);
    }

    ngOnInit(): void {

        this.language = localStorage.getItem('selectedLang');

        this.token = this.route.snapshot.queryParamMap.get('link');

        const nameFromQuery = this.route.snapshot.queryParamMap.get('name');
        const emailFromQuery = this.route.snapshot.queryParamMap.get('email');

        if (nameFromQuery) this.userName = decodeURIComponent(nameFromQuery);
        if (emailFromQuery) this.userEmail = decodeURIComponent(emailFromQuery);

        if (!this.token) {
            this.invalidLink = true;
        }

        this.resetForm = this.fb.group({
            name: [{ value: '', disabled: true }],
            email: [{ value: '', disabled: true }],
            password: ['', [Validators.required, Validators.minLength(8)]],
            confirmPassword: ['', Validators.required],
            acceptTerms: [true]
        });

        this.fetchUserDetails();
    }


    fetchUserDetails() {
        this._service
            .getService(this._service.API_URL.resetPasswordDetails + "?token=" + this.token)
            .subscribe(
                (res: any) => {
                    // VALID TOKEN → show reset form
                    if (res.status.code === 200 && res.data) {
                        this.userName = res.data.name;
                        this.userEmail = res.data.email;

                        this.resetForm.patchValue({
                            name: this.userName,
                            email: this.userEmail
                        });
                        return;
                    }

                    if (res.status.msgCode === 'PASSWORD_ALREADY_RESET' || res.status.msgCode === 'QH_TOKEN_EXPIRED' || res.status.msgCode === 'QH_INVALID_STATIC_REQUEST_TOKEN') {
                        this.commonService.getLimit.next("URL_EXPIRED");
                        this.router.navigateByUrl('/link-expired');
                        return;
                    }
                },
                (err) => {
                    const code = err?.error?.status?.msgCode;

                    this.commonService.getLimit.next("URL_EXPIRED");
                    this.router.navigateByUrl('/link-expired');
                }
            );
    }



    // getters for template
    get f() {
        return this.resetForm.controls;
    }

    toggleNewPwd() {
        this.newPasswordVisible = !this.newPasswordVisible;
    }

    toggleConfirmPwd() {
        this.confirmPasswordVisible = !this.confirmPasswordVisible;
    }

    submit(): void {
        this.errorMessage = '';
        if (this.invalidLink) {
            this.errorMessage = this.translate.instant('RESET_PASSWORD.INVALID_LINK');
            return;
        }

        if (this.resetForm.invalid) {
            this.resetForm.markAllAsTouched();
            return;
        }

        const pwd = this.resetForm.value.password;
        const confirm = this.resetForm.value.confirmPassword;

        if (pwd !== confirm) {
            this.errorMessage = this.translate.instant('RESET_PASSWORD.PASS_NOT_MATCH');
            return;
        }

        this.loading = true;

        const body = {
            token: this.token,
            password: pwd,
            confirmPassword: confirm
        };

        const randomUid = localStorage.getItem('randomUid') || '';

        const headers = {
            'RANDOM-UID': randomUid
        };

        this._service.postService(this._service.API_URL.resetPassword, body, { headers })
            .subscribe({
                next: (response: any) => {
                    this.loading = false;
                    if (response && response.status && response.status.code === 200) {
                        const msg = this.translate.instant('RESET_PASSWORD.PASS_CHANGED_SUCCESSFULLY');
                        this.toastr.success('', response.status.message || msg);
                        try { localStorage.removeItem('jwtToken'); localStorage.removeItem('Authorization'); } catch (e) { }

                        this.router.navigate(['/']);
                    } else {
                        const msg = this.translate.instant('RESET_PASSWORD.PASS_RESET_FAILED');
                        this.errorMessage = response?.status?.message || msg;
                    }
                },
                error: (err) => {
                    this.loading = false;

                    const msg = err?.error?.status?.msgCode;

                    if (msg === 'PASSWORD_ALREADY_RESET') {
                        const text = this.translate.instant('RESET_PASSWORD.PASSWORD_ALREADY_RESET');
                        this.toastr.info(text);
                        this.router.navigate(['/']);
                        return;
                    }

                    const fallback = this.translate.instant('RESET_PASSWORD.SERVER_ERROR');
                    this.errorMessage = err?.error?.status?.message
                        ? this.translate.instant(err.error.status.message)
                        : fallback;
                }
            });
    }
}
